import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * واجهة إعدادات صور الصيانة
 */
export interface MaintenanceImageSettings {
  provider: 'imgbb' | 'imagekit' | 'cloudinary';
  compressionEnabled: boolean;
  compressionQuality: number;
  maxImageSize: number;
  autoUpload: boolean;
  fallbackProvider?: 'imgbb' | 'imagekit' | 'cloudinary';
  retryAttempts?: number;
}

// الإعدادات الافتراضية
const DEFAULT_MAINTENANCE_IMAGE_SETTINGS: MaintenanceImageSettings = {
  provider: 'imgbb',
  compressionEnabled: true,
  compressionQuality: 80,
  maxImageSize: 1920,
  autoUpload: true,
  fallbackProvider: 'imgbb',
  retryAttempts: 3
};

/**
 * الحصول على إعدادات صور الصيانة من Firebase
 * @returns وعد بإعدادات صور الصيانة
 */
export const getMaintenanceImageSettings = async (): Promise<MaintenanceImageSettings> => {
  try {
    console.log('🔄 جاري تحميل إعدادات صور الصيانة من Firebase...');
    
    // محاولة الحصول على الإعدادات من Firestore
    const settingsDoc = await getDoc(doc(db, 'settings', 'maintenanceImages'));

    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as MaintenanceImageSettings;
      console.log('✅ تم تحميل إعدادات صور الصيانة من Firebase:', data);
      
      return {
        provider: data.provider || DEFAULT_MAINTENANCE_IMAGE_SETTINGS.provider,
        compressionEnabled: data.compressionEnabled ?? DEFAULT_MAINTENANCE_IMAGE_SETTINGS.compressionEnabled,
        compressionQuality: data.compressionQuality || DEFAULT_MAINTENANCE_IMAGE_SETTINGS.compressionQuality,
        maxImageSize: data.maxImageSize || DEFAULT_MAINTENANCE_IMAGE_SETTINGS.maxImageSize,
        autoUpload: data.autoUpload ?? DEFAULT_MAINTENANCE_IMAGE_SETTINGS.autoUpload,
        fallbackProvider: data.fallbackProvider || DEFAULT_MAINTENANCE_IMAGE_SETTINGS.fallbackProvider || 'imgbb',
        retryAttempts: data.retryAttempts || DEFAULT_MAINTENANCE_IMAGE_SETTINGS.retryAttempts || 3
      };
    }

    console.log('📄 لا توجد إعدادات محفوظة، استخدام الإعدادات الافتراضية');
    return DEFAULT_MAINTENANCE_IMAGE_SETTINGS;
  } catch (error) {
    console.error('❌ خطأ في تحميل إعدادات صور الصيانة:', error);
    return DEFAULT_MAINTENANCE_IMAGE_SETTINGS;
  }
};

/**
 * حفظ إعدادات صور الصيانة في Firebase
 * @param settings إعدادات صور الصيانة
 * @returns وعد بنتيجة عملية الحفظ
 */
export const saveMaintenanceImageSettings = async (settings: MaintenanceImageSettings): Promise<void> => {
  try {
    console.log('💾 جاري حفظ إعدادات صور الصيانة في Firebase:', settings);
    
    await setDoc(doc(db, 'settings', 'maintenanceImages'), {
      ...settings,
      updatedAt: new Date()
    });

    console.log('✅ تم حفظ إعدادات صور الصيانة بنجاح');
  } catch (error) {
    console.error('❌ خطأ في حفظ إعدادات صور الصيانة:', error);
    throw error;
  }
};

/**
 * التحقق من توفر مزود الخدمة
 * @param provider مزود الخدمة
 * @returns وعد بحالة التوفر
 */
export const checkProviderAvailability = async (provider: 'imgbb' | 'imagekit' | 'cloudinary'): Promise<boolean> => {
  try {
    switch (provider) {
      case 'imgbb':
        // التحقق من توفر ImgBB (يمكن استخدامه دائماً)
        return true;
        
      case 'imagekit':
        // التحقق من إعدادات ImageKit
        const imagekitDoc = await getDoc(doc(db, 'settings', 'imagekit'));
        if (imagekitDoc.exists()) {
          const data = imagekitDoc.data();
          return !!(data.publicKey && data.privateKey && data.urlEndpoint && data.enabled);
        }
        return false;
        
      case 'cloudinary':
        // التحقق من إعدادات Cloudinary
        const cloudinaryDoc = await getDoc(doc(db, 'settings', 'cloudinary'));
        if (cloudinaryDoc.exists()) {
          const data = cloudinaryDoc.data();
          return !!(data.cloudName && data.apiKey && data.apiSecret);
        }
        return false;
        
      default:
        return false;
    }
  } catch (error) {
    console.error(`خطأ في التحقق من توفر ${provider}:`, error);
    return false;
  }
};

/**
 * الحصول على مزود الخدمة المتاح
 * @returns وعد بمزود الخدمة المتاح
 */
export const getAvailableProvider = async (): Promise<'imgbb' | 'imagekit' | 'cloudinary'> => {
  try {
    const settings = await getMaintenanceImageSettings();
    
    // التحقق من المزود المفضل
    const isPreferredAvailable = await checkProviderAvailability(settings.provider);
    if (isPreferredAvailable) {
      console.log(`✅ المزود المفضل ${settings.provider} متاح`);
      return settings.provider;
    }
    
    // التحقق من المزود البديل
    if (settings.fallbackProvider) {
      const isFallbackAvailable = await checkProviderAvailability(settings.fallbackProvider);
      if (isFallbackAvailable) {
        console.log(`✅ المزود البديل ${settings.fallbackProvider} متاح`);
        return settings.fallbackProvider;
      }
    }
    
    // التحقق من جميع المزودين المتاحين
    const providers: ('imgbb' | 'imagekit' | 'cloudinary')[] = ['imgbb', 'imagekit', 'cloudinary'];
    for (const provider of providers) {
      const isAvailable = await checkProviderAvailability(provider);
      if (isAvailable) {
        console.log(`✅ المزود ${provider} متاح كبديل`);
        return provider;
      }
    }
    
    // في حالة عدم توفر أي مزود، استخدم ImgBB كافتراضي
    console.log('⚠️ لا يوجد مزود متاح، استخدام ImgBB كافتراضي');
    return 'imgbb';
  } catch (error) {
    console.error('❌ خطأ في الحصول على مزود الخدمة المتاح:', error);
    return 'imgbb';
  }
};

/**
 * تحديث مزود الخدمة فقط
 * @param provider مزود الخدمة الجديد
 */
export const updateImageProvider = async (provider: 'imgbb' | 'imagekit' | 'cloudinary'): Promise<void> => {
  try {
    const currentSettings = await getMaintenanceImageSettings();
    await saveMaintenanceImageSettings({
      ...currentSettings,
      provider
    });
  } catch (error) {
    console.error('خطأ في تحديث مزود الخدمة:', error);
    throw error;
  }
};

export default {
  getMaintenanceImageSettings,
  saveMaintenanceImageSettings,
  checkProviderAvailability,
  getAvailableProvider,
  updateImageProvider,
  DEFAULT_MAINTENANCE_IMAGE_SETTINGS
};
